import { CheckBox } from '../com/BoxesGS';
import { Valued } from "../core/WrapperGS";
import { toType } from './def';
import { getLang, normalizeLang, setLang } from './localization';

export let validPhoneReg = '^[0-9]+$';
export let validEmailReg = '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$';

const messages_ru={
    notFoundSameField:"Не найдено поле для сравнения!",
    noValue:"Введите значение!",
    noSelectedValue:"Укажите значение!",
    noNumber:"Введите число!",
    noIntNumber:"Нужно ввести целое число!",
    noUIntNumber:"Нужно ввести целое неотрицательное число!",
    noPhone:"Введите номер телефона в нужном формате!",
    noEmail:"Введите email в нужном формате!",
    noSame:"Введите одинаковое значение!",
    noFoto:"Загрузите фото!",
    invalidRuleArgument:"Некорректный аргумент правила валидации!",
    unknownRule:"Неизвестное правило валидации: {arg0}",
    unknownFunction:"Неизвестная функция валидации: {arg0}",
    maxValue:"Максимальное значение {arg0}!",
    minValue:"Минимальное значение {arg0}!",
    notOnlyNumber:"Введите не только цифры!",
    fixCountChars: "Нужно ввести {arg0} символа!",
    fixCountElements: "Нужно {arg0} элементов!",
    noMoreChars:"Не более {arg0} символов!",
    noMoreElements:"Не более {arg0} элементов!",
    noLessChars:"Не менее {arg0} символов!",
    noLessElements:"Не менее {arg0} элементов!",
}
const messages_en={
    notFoundSameField: "Comparison field not found!",
    noValue: "Please enter a value!",
    noSelectedValue: "Please select a value!",
    noNumber: "Please enter a number!",
    noIntNumber: "Please enter an integer!",
    noUIntNumber: "Please enter a non-negative integer!",
    noPhone: "Please enter a phone number in the correct format!",
    noEmail: "Please enter an email in the correct format!",
    noSame: "Please enter the same value!",
    noFoto: "Please upload a photo!",
    invalidRuleArgument: "Invalid validation rule argument!",
    unknownRule: "Unknown validation rule: {arg0}",
    unknownFunction: "Unknown validation function: {arg0}",
    maxValue: "Maximum value is {arg0}!",
    minValue: "Minimum value is {arg0}!",
    notOnlyNumber: "Please enter more than just digits!",
    fixCountChars: "You need to enter {arg0} characters!",
    fixCountElements: "You need {arg0} elements!",
    noMoreChars: "No more than {arg0} characters!",
    noMoreElements: "No more than {arg0} elements!",
    noLessChars: "At least {arg0} characters!",
    noLessElements: "At least {arg0} elements!",
}
export type ValidationMessagesGS = typeof messages_en;

const messageSets = new Map<string, Readonly<ValidationMessagesGS>>([
    ['en', messages_en],
    ['ru', messages_ru]
]);

/** Messages resolved against the current browser or server request language. */
export const messages: Readonly<ValidationMessagesGS> = new Proxy({} as ValidationMessagesGS, {
    get: (_target, property) => Reflect.get(getMessages(), property),
    set: () => false,
    ownKeys: () => Reflect.ownKeys(messages_en),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true })
});

export function getMessages(lang = getLang()): Readonly<ValidationMessagesGS> {
    const normalized = normalizeLang(lang);
    return messageSets.get(normalized)
        ?? messageSets.get(normalized.split('-')[0] ?? '')
        ?? messages_en;
}

export function registerMessages(lang: string, values: ValidationMessagesGS): void {
    messageSets.set(normalizeLang(lang), Object.freeze({ ...values }));
}

/** Compatibility alias; language selection is now request scoped. */
export function localize(lang: string): void { setLang(lang); }

function replace(str:string, value:any) { return str.replace("{arg0}",value+""); }
function _noValue(wr:Valued)
{
    if (!wr) return true;
    const value = wr.value;
    return value === undefined
        || value === null
        || value === ""
        || (Array.isArray(value) && value.length === 0);
}

export function isNumber(val: unknown): boolean {
    if (typeof val === 'number') return Number.isFinite(val);
    if (typeof val !== 'string') return false;
    const normalized = val.trim().replace(/,/g, '.');
    return /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(normalized)
        && Number.isFinite(Number(normalized));
}

export function valueRequired(wr: Valued): boolean | string {
    if (!wr) return messages.noValue;
    const checkBox = toType(wr, CheckBox);
    if (checkBox) {
        if (checkBox.value.length === 0) return messages.noSelectedValue;
        return true;
    }
    if (_noValue(wr)) return messages.noValue;
    return true;
}

export function valueIsNumber(wr: Valued): boolean | string {    
    if (_noValue(wr)) return messages.noNumber;
    const value = typeof wr.value === 'string'
        ? wr.value.trim().replace(/,/g, '.')
        : wr.value;
    if (!isNumber(value)) return messages.noNumber;
    if (typeof wr.value === 'string') wr.value = value;
    return true;
}

export function valueNumberMax(wr: Valued, max: number): boolean | string {
    if (!Number.isFinite(max)) return messages.invalidRuleArgument;
    const valid = valueIsNumber(wr);
    if (typeof valid === "string") return valid;
    const value = Number(wr.value);
    if (value > max) return replace(messages.maxValue, max);
    return true;
}
export function valueNumberMin(wr: Valued, min: number): boolean | string {
    if (!Number.isFinite(min)) return messages.invalidRuleArgument;
    const valid = valueIsNumber(wr);
    if (typeof valid === "string") return valid;
    const value = Number(wr.value);
    if (value < min) return replace(messages.minValue, min);
    return true;
}
export function valueNumberInt(wr: Valued): boolean | string {
    const valid = valueIsNumber(wr);
    if (typeof valid === "string") return valid;
    if (!Number.isInteger(Number(wr.value))) return messages.noIntNumber;
    return true;
}
export function valueNumberUInt(wr: Valued): boolean | string {
    const valid = valueNumberInt(wr);
    if (typeof valid === "string") return valid;    
    if (Number(wr.value) < 0) return messages.noUIntNumber;
    return true;
}
export function valueNotNumber(wr: Valued): boolean | string {
    if (_noValue(wr)) return true;
    return isNumber(typeof wr.value === 'string' ? wr.value : String(wr.value))
        ? messages.notOnlyNumber
        : true;
}
export function valueLength(wr: Valued, length: number): boolean | string {
    if (!isValidLength(length)) return messages.invalidRuleArgument;
    const valid = valueRequired(wr);    
    if (typeof valid === "string") return valid;
    if (typeof wr.value === "string") {
        if (wr.value.length !== length) return replace(messages.fixCountChars, length);
    }    
    if (Array.isArray(wr.value)) {
        if (wr.value.length !== length) return replace(messages.fixCountElements, length); 
    }
    return true;
}
export function valueMinLength(wr: Valued, length: number): boolean | string {
    if (!isValidLength(length)) return messages.invalidRuleArgument;
    const valid = valueRequired(wr);
    if (typeof valid === "string") return valid;
    if (typeof wr.value === "string") {
        if (wr.value.length < length) return replace(messages.noLessChars, length);
    }
    if (Array.isArray(wr.value)) {
        if (wr.value.length < length) return replace(messages.noLessElements, length);
    }
    return true;
}
export function valueMaxLength(wr: Valued, length: number): boolean | string {
    if (!isValidLength(length)) return messages.invalidRuleArgument;
    if (_noValue(wr)) return true;
    if (typeof wr.value === "string") {
        if (wr.value.length > length) return replace(messages.noMoreChars, length);
    }
    if (Array.isArray(wr.value)) {
        if (wr.value.length > length) return replace(messages.noMoreElements, length);
    }
    return true;
}
export function valueIsPhone(wr: Valued): boolean | string {
    if (_noValue(wr)) return true;
    if (!new RegExp(validPhoneReg).test(String(wr.value))) return messages.noPhone;
    return true;
}
export function valueIsEmail(wr: Valued): boolean | string {
    if (_noValue(wr)) return true;
    if (!new RegExp(validEmailReg).test(String(wr.value))) return messages.noEmail;
    return true;
}

export function valueSame(wr: Valued, wrAnother: Valued): boolean | string {
    if (wr.value !== wrAnother.value) return messages.noSame;
    return true;
}

function isValidLength(value: number): boolean {
    return Number.isInteger(value) && value >= 0;
}

